<!-- components/Navbar.vue -->
<template>
  <nav class="navbar">
    <div class="nav-left">
      <button
        class="menu-toggle"
        @click="toggleMobileMenu"
        aria-label="Toggle menu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>
      <div class="brand" @click="$router.push('/')">
        <img src="@/assets/logo-myharf-x.png" alt="" class="logo" />
      </div>
    </div>

    <div class="nav-center">
      <div class="search-wrapper">
        <input
          type="text"
          class="searchbar"
          :placeholder="
            isRTL ? 'ماذا تريد ان تتعلم؟' : 'What do you want to learn?'
          "
          v-model="searchQuery"
          @keyup.enter="handleSearch"
          :dir="isRTL ? 'rtl' : 'ltr'"
        />
        <button class="searchbtn" @click="handleSearch" aria-label="Search">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </button>
      </div>
    </div>

    <div class="nav-right">
      <!-- Create Button -->
      <button
        v-if="isAuthenticated"
        class="nav-btn create-btn"
        @click="handleCreate"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Create</span>
      </button>

      <!-- Authenticated User -->
      <div v-if="isAuthenticated" class="user-menu">
        <button
          class="nav-btn notifications"
          @click="toggleNotifications"
          aria-label="Notifications"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <span class="notification-badge" v-if="notificationCount > 0">{{
            notificationCount
          }}</span>
        </button>

        <div class="avatar-wrapper" @click="toggleUserMenu">
          <img v-if="userAvatar" :src="userAvatar" alt="User avatar" />
          <div v-else class="avatar-placeholder">{{ userInitials }}</div>
        </div>
        <!-- components/Navbar.vue - Update dropdown -->
        <div class="user-dropdown" v-if="showUserMenu" @click.stop>
          <div class="dropdown-header">
            <p class="username">{{ userName }}</p>
            <p class="user-email">{{ userEmail }}</p>
          </div>
          <div class="dropdown-divider"></div>
          <router-link
            class="dropdown-item"
            to="/profile"
            @click="closeDropdown"
          >
            👤 My Profile
          </router-link>
          <router-link class="dropdown-item" to="/feed" @click="closeDropdown">
            📰 My Feed
          </router-link>
          <router-link
            class="dropdown-item"
            to="/studio"
            @click="closeDropdown"
          >
            🎓 Course Studio
          </router-link>
          <div class="dropdown-divider"></div>
          <button class="dropdown-item signout" @click="handleSignOut">
            🚪 Sign Out
          </button>
        </div>
      </div>

      <!-- Unauthenticated User -->
      <button v-else class="nav-btn signin-btn" @click="openAuthModal">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <polyline points="10 17 15 12 10 7" />
          <line x1="15" y1="12" x2="3" y2="12" />
        </svg>
        <span>Sign In</span>
      </button>
    </div>

    <!-- Mobile Menu -->
    <Transition name="slide-down">
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <div class="mobile-search">
          <input
            type="text"
            placeholder="Search..."
            v-model="searchQuery"
            @keyup.enter="handleSearch"
          />
          <button @click="handleSearch">Search</button>
        </div>
        <div class="mobile-links">
          <router-link to="/" @click="closeMobileMenu">🏠 Home</router-link>
          <router-link to="/explore" @click="closeMobileMenu"
            >📚 Explore</router-link
          >
          <router-link to="/about" @click="closeMobileMenu"
            >ℹ️ About</router-link
          >
          <router-link
            v-if="isAuthenticated"
            to="/feed"
            @click="closeMobileMenu"
            >📰 Feed</router-link
          >
          <router-link
            v-if="isAuthenticated"
            to="/studio"
            @click="closeMobileMenu"
            >🎓 Studio</router-link
          >
        </div>
        <div v-if="!isAuthenticated" class="mobile-auth">
          <button class="btn-primary" @click="openAuthModal">Sign In</button>
        </div>
      </div>
    </Transition>
  </nav>

  <!-- Auth Modal -->
  <AuthModal v-if="showAuthModal" @close="closeAuthModal" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import AuthModal from "@/components/AuthModal.vue";

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// State
const searchQuery = ref("");
const mobileMenuOpen = ref(false);
const showUserMenu = ref(false);
const showAuthModal = ref(false);
const notificationCount = ref(3);

// Computed
const isAuthenticated = computed(() => authStore.isAuthenticated);
const user = computed(() => authStore.user);
const userName = computed(
  () => user.value?.displayName || user.value?.email?.split("@")[0] || "User",
);
const userEmail = computed(() => user.value?.email || "");
const userAvatar = computed(() => user.value?.photoURL || null);
const userInitials = computed(() => {
  if (!userName.value) return "?";
  return userName.value
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
});
const isRTL = computed(() => document.dir === "rtl");

// Watch for route changes to close mobile menu
watch(
  () => route.path,
  () => {
    closeMobileMenu();
    closeDropdown();
  },
);

// Methods
const handleSearch = () => {
  if (searchQuery.value.trim()) {
    router.push({ path: "/search", query: { q: searchQuery.value } });
    searchQuery.value = "";
    closeMobileMenu();
  }
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  if (mobileMenuOpen.value) {
    showUserMenu.value = false;
  }
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
  if (showUserMenu.value) {
    mobileMenuOpen.value = false;
  }
};

const closeDropdown = () => {
  showUserMenu.value = false;
};

const toggleNotifications = () => {
  console.log("Open notifications");
};

const openAuthModal = () => {
  showAuthModal.value = true;
  closeMobileMenu();
};

const closeAuthModal = () => {
  showAuthModal.value = false;
};

const handleCreate = () => {
  router.push("/studio");
  closeMobileMenu();
};

const handleSignOut = async () => {
  await authStore.signOut();
  showUserMenu.value = false;
  router.push("/");
};

// Click outside to close dropdown
const handleClickOutside = (event) => {
  const dropdown = document.querySelector(".user-dropdown");
  const avatar = document.querySelector(".avatar-wrapper");
  if (
    dropdown &&
    !dropdown.contains(event.target) &&
    avatar &&
    !avatar.contains(event.target)
  ) {
    showUserMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  // Close mobile menu on resize
  const handleResize = () => {
    if (window.innerWidth > 768) {
      closeMobileMenu();
    }
  };
  window.addEventListener("resize", handleResize);

  // Cleanup
  onUnmounted(() => {
    document.removeEventListener("click", handleClickOutside);
    window.removeEventListener("resize", handleResize);
  });
});
</script>

<style lang="scss" scoped>
$navheight: 72px;
$primary-orange: #ff8000;
$primary-blue: #001829;
$hover-orange: #cc5800;

.navbar {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  padding: 0 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: $navheight;
  background: $primary-blue;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
}

/* Left Section */
.nav-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 150px;
  flex-shrink: 0;
}

.user-menu {
  display: flex;
  gap: 16px;
}

.menu-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 8px;
  background: none;
  border: none;

  span {
    display: block;
    width: 24px;
    height: 2.5px;
    background: #fff;
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  &:hover span {
    background: $primary-orange;
  }

  @media (max-width: 768px) {
    display: flex;
  }
}

.brand {
  display: flex;
  align-items: center;
  gap: 16px;
  .logo {
    max-height: calc($navheight * 0.618);
    background: #fff;
    padding: 4px;
    border-radius: 8px;

    cursor: pointer;
    transition: transform 0.2s ease;
    &:hover {
      transform: scale(1.05);
    }
  }
  .name {
    font-size: 26px;
    font-weight: 800;
    cursor: pointer;
    letter-spacing: -0.5px;
    transition: transform 0.2s ease;
    display: flex;
    align-items: center;
    gap: 2px;

    &:hover {
      transform: scale(1.05);
    }

    .my {
      background: linear-gradient(135deg, $primary-orange, #ffa500);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .harf {
      color: #fff;
      -webkit-text-fill-color: #fff;
    }

    @media (max-width: 480px) {
      font-size: 20px;
    }
  }
}
/* Center Section */
.nav-center {
  flex: 1;
  max-width: 640px;
  margin: 0 24px;

  @media (max-width: 768px) {
    margin: 0 12px;
    display: none;
  }
}

.search-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.08);

  &:focus-within {
    background: rgba(255, 255, 255, 0.18);
    border-color: $primary-orange;
    box-shadow: 0 0 0 3px rgba(255, 128, 0, 0.15);
  }

  .searchbar {
    flex: 1;
    padding: 10px 16px;
    background: transparent;
    border: none;
    color: #fff;
    font-size: 15px;
    outline: none;
    min-width: 0;

    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .searchbtn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 16px;
    background: transparent;
    border: none;
    border-radius: 0 24px 24px 0;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      color: #fff;
      background: rgba(255, 128, 0, 0.2);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }
}

/* Right Section */
.nav-right {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 150px;
  justify-content: flex-end;
  flex-shrink: 0;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  svg {
    width: 18px;
    height: 18px;
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid $primary-orange;
    outline-offset: 2px;
  }
}

.create-btn {
  background: rgba(255, 128, 0, 0.15);
  color: $primary-orange;
  border: 1px solid rgba(255, 128, 0, 0.3);

  &:hover {
    background: rgba(255, 128, 0, 0.25);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 8px 12px;
    span {
      display: none;
    }
  }
}

.signin-btn {
  background: $primary-orange;
  color: #fff;

  &:hover {
    background: $hover-orange;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 128, 0, 0.3);
  }
}

.notifications {
  position: relative;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  padding: 8px;
  border-radius: 50%;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  .notification-badge {
    position: absolute;
    top: 2px;
    right: 2px;
    background: #ff4444;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 50%;
    min-width: 18px;
    text-align: center;
    border: 2px solid $primary-blue;
  }
}

/* User Avatar */
.avatar-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    border-color: $primary-orange;
    transform: scale(1.05);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, $primary-orange, #ffa500);
    color: #fff;
    font-weight: 700;
    font-size: 14px;
  }
}

/* User Dropdown */
.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  min-width: 240px;
  overflow: hidden;
  animation: slideDown 0.2s ease;

  .dropdown-header {
    padding: 16px;

    .username {
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 4px 0;
    }

    .user-email {
      font-size: 13px;
      color: #666;
      margin: 0;
      word-break: break-all;
    }
  }

  .dropdown-divider {
    height: 1px;
    background: #eee;
    margin: 0 12px;
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 16px;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      background: #f5f5f5;
    }

    &.signout {
      color: #ff4444;
      font-weight: 600;

      &:hover {
        background: #fff0f0;
      }
    }
  }
}

/* Mobile Menu */
.mobile-menu {
  position: absolute;
  top: $navheight;
  left: 0;
  width: 100%;
  background: $primary-blue;
  padding: 16px 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  .mobile-search {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;

    input {
      flex: 1;
      padding: 10px 16px;
      border: none;
      border-radius: 24px;
      background: rgba(255, 255, 255, 0.12);
      color: #fff;
      font-size: 15px;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }

      &:focus {
        outline: 2px solid $primary-orange;
      }
    }

    button {
      padding: 10px 20px;
      background: $primary-orange;
      border: none;
      border-radius: 24px;
      color: #fff;
      font-weight: 600;
      cursor: pointer;

      &:hover {
        background: $hover-orange;
      }
    }
  }

  .mobile-links {
    display: flex;
    flex-direction: column;
    gap: 4px;

    a {
      padding: 10px 12px;
      color: rgba(255, 255, 255, 0.8);
      text-decoration: none;
      border-radius: 8px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      gap: 10px;

      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
      }

      &.router-link-active {
        background: rgba(255, 128, 0, 0.15);
        color: $primary-orange;
      }
    }
  }

  .mobile-auth {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);

    .btn-primary {
      width: 100%;
      padding: 12px;
      background: $primary-orange;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-weight: 600;
      font-size: 16px;
      cursor: pointer;

      &:hover {
        background: $hover-orange;
      }
    }
  }
}

/* Transitions */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .nav-center {
    display: none;
  }

  .nav-right {
    .create-btn span {
      display: none;
    }
  }

  .user-dropdown {
    right: 16px;
    left: 16px;
    min-width: unset;
  }
}

@media (max-width: 480px) {
  .navbar {
    padding: 0 12px;
  }

  .nav-right {
    gap: 4px;
  }

  .nav-btn {
    padding: 6px 10px;
    font-size: 13px;
  }

  .avatar-wrapper {
    width: 32px;
    height: 32px;
  }
}
</style>
